import { Link } from "react-router-dom";
import { PublicHeader } from "@/components/marketing/PublicHeader";
import { PublicFooter } from "@/components/marketing/PublicFooter";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import {
  Shield,
  CheckCircle2,
  AlertTriangle,
  FileCheck,
  Star,
  Clock,
  Users,
  ArrowRight,
  BadgeCheck,
  Building2,
  Scale,
  FileText,
  BarChart3,
  TrendingUp,
  Award,
  Info,
} from "lucide-react";

export default function Verification() {
  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Vendor Verification - How We Build Trust"
        description="Learn how Radius verifies vendors through enrollment checks and tracks platform performance data to help you make informed decisions."
        canonical="/verification"
      />
      <PublicHeader />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-primary/5 via-background to-background">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--primary)/0.1),transparent_50%)]" />
        <div className="container relative py-12 md:py-20">
          <div className="mx-auto max-w-4xl text-center space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
              <Shield className="h-4 w-4" />
              Building Trust Through Transparency
            </div>
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
              How We <span className="text-primary">Verify Vendors</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Radius uses a two-tier approach to help you make informed vendor decisions: 
              enrollment verification when vendors join, and platform performance data as they complete work.
            </p>
          </div>
        </div>
      </section>

      {/* Two-Tier Approach Overview */}
      <section className="container py-20">
        <div className="text-center space-y-4 mb-12">
          <h2 className="text-3xl font-bold">Our Two-Tier Approach</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            We combine initial verification with ongoing performance tracking
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          <div className="p-8 rounded-2xl border bg-card shadow-sm">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 rounded-xl bg-primary/10">
                <BadgeCheck className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-semibold">Tier 1: Enrollment Verification</h3>
                <p className="text-sm text-muted-foreground">Checked when vendors join</p>
              </div>
            </div>
            <p className="text-muted-foreground mb-4">
              Every vendor undergoes verification checks before they can bid on jobs. 
              This confirms basic business legitimacy and capability claims.
            </p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <span>Business registration confirmed</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <span>Tax ID and W-9 on file</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <span>Insurance documentation reviewed</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <span>Capability claims documented</span>
              </li>
            </ul>
          </div>

          <div className="p-8 rounded-2xl border bg-card shadow-sm">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 rounded-xl bg-primary/10">
                <BarChart3 className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-semibold">Tier 2: Platform Performance</h3>
                <p className="text-sm text-muted-foreground">Tracked over time</p>
              </div>
            </div>
            <p className="text-muted-foreground mb-4">
              As vendors complete work on the platform, we track real performance data 
              that reflects their actual track record with Radius clients.
            </p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-sm">
                <Star className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <span>Client ratings and reviews</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Clock className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <span>On-time delivery rate</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <TrendingUp className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <span>Jobs completed successfully</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Users className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <span>Repeat client percentage</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* What We Check Section */}
      <section className="bg-muted/50 py-20">
        <div className="container">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl font-bold">Enrollment Verification Details</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              What we verify before vendors can access the platform
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="p-6 rounded-lg border bg-background">
              <Building2 className="h-8 w-8 text-primary mb-4" />
              <h3 className="font-semibold mb-2">Business Registration</h3>
              <p className="text-sm text-muted-foreground">
                We confirm the business is legally registered and operating in good standing.
              </p>
            </div>
            <div className="p-6 rounded-lg border bg-background">
              <FileText className="h-8 w-8 text-primary mb-4" />
              <h3 className="font-semibold mb-2">Tax Documentation</h3>
              <p className="text-sm text-muted-foreground">
                Valid tax identification and W-9 forms are collected and verified.
              </p>
            </div>
            <div className="p-6 rounded-lg border bg-background">
              <Shield className="h-8 w-8 text-primary mb-4" />
              <h3 className="font-semibold mb-2">Insurance Coverage</h3>
              <p className="text-sm text-muted-foreground">
                General liability and other relevant insurance documentation is reviewed.
              </p>
            </div>
            <div className="p-6 rounded-lg border bg-background">
              <Award className="h-8 w-8 text-primary mb-4" />
              <h3 className="font-semibold mb-2">Certifications</h3>
              <p className="text-sm text-muted-foreground">
                Claimed certifications (FSC, ISO, diversity certifications, etc.) are documented.
              </p>
            </div>
            <div className="p-6 rounded-lg border bg-background">
              <FileCheck className="h-8 w-8 text-primary mb-4" />
              <h3 className="font-semibold mb-2">Capability Claims</h3>
              <p className="text-sm text-muted-foreground">
                Self-reported capabilities are collected and displayed transparently.
              </p>
            </div>
            <div className="p-6 rounded-lg border bg-background">
              <Scale className="h-8 w-8 text-primary mb-4" />
              <h3 className="font-semibold mb-2">Terms Agreement</h3>
              <p className="text-sm text-muted-foreground">
                Vendors agree to platform terms including quality standards and dispute resolution.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Performance Data */}
      <section className="container py-20">
        <div className="text-center space-y-4 mb-12">
          <h2 className="text-3xl font-bold">Platform Performance Data</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Real data from actual transactions on Radius
          </p>
        </div>
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                  <Star className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Client Ratings</h3>
                  <p className="text-sm text-muted-foreground">
                    After each completed job, clients rate vendors on quality, communication, 
                    and overall satisfaction. Only verified clients who completed transactions can leave reviews.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                  <Clock className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Delivery Performance</h3>
                  <p className="text-sm text-muted-foreground">
                    We track on-time delivery rates based on agreed milestone deadlines. 
                    This data reflects actual performance, not self-reported claims.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                  <TrendingUp className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Transaction History</h3>
                  <p className="text-sm text-muted-foreground">
                    Number of completed jobs, total transaction value, and success rate 
                    are calculated from actual platform activity.
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Repeat Business</h3>
                  <p className="text-sm text-muted-foreground">
                    We show what percentage of a vendor's clients return for additional work—a 
                    strong indicator of satisfaction.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                  <AlertTriangle className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Dispute History</h3>
                  <p className="text-sm text-muted-foreground">
                    Any disputes and their resolutions are factored into vendor standing, 
                    though specific details remain confidential.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                  <BarChart3 className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Response Time</h3>
                  <p className="text-sm text-muted-foreground">
                    Average time to respond to RFQs and client messages helps you 
                    gauge vendor responsiveness.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Important Limitations */}
      <section className="border-y bg-amber-50/50 dark:bg-amber-950/20 py-16">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4 mb-8">
              <div className="p-3 rounded-xl bg-amber-100 dark:bg-amber-900/30 shrink-0">
                <Info className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold mb-2">Important Limitations</h2>
                <p className="text-muted-foreground">
                  We believe in transparency about what verification can and cannot guarantee.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 rounded-lg border bg-background">
                <h3 className="font-semibold mb-3 text-amber-700 dark:text-amber-400">What Verification Doesn't Guarantee</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Future performance or quality of work</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Suitability for your specific project</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Financial stability or long-term viability</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Claims made outside our platform</span>
                  </li>
                </ul>
              </div>
              <div className="p-6 rounded-lg border bg-background">
                <h3 className="font-semibold mb-3 text-amber-700 dark:text-amber-400">New Vendors</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>New vendors have verified enrollment but limited platform history</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Performance data builds over time as they complete jobs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Consider starting with smaller projects for new vendor relationships</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Use platform messaging to assess responsiveness before awarding</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* CTA */}
      <section className="border-y bg-primary/5 py-20">
        <div className="container text-center space-y-6">
          <h2 className="text-3xl font-bold">Ready to Find Verified Vendors?</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Browse our marketplace of verified vendors with transparent performance data.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild>
              <Link to="/auth/signup?type=buyer">
                Get Started <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/for-clients">
                Learn More for Buyers
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
